// src/pages/MyPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";

import Header from "components/Header";
import Footer from "components/Footer";

/* =========================
 * API ベースURL
 * ========================= */
// ブラウザからは常に同一オリジンの /api を叩く
const API_BASE = "";

/* =========================
 * 型
 * ========================= */
type UserInfo = {
  id: number | string;
  userId?: string | number;
  name?: string;
  points?: number;
};

type Purchase = {
  id: string | number;
  planId: number;
  planName: string;
  purchasedAt: string;
  publishAt?: string;
  publishTime?: string; // ★ adminPlans.json の publishTime 用
  // 追加情報（ポイント購入判定用）
  isPointPurchase?: boolean;
  pointAmount?: number; // ※サーバーがくれたらそのまま入れるだけ（計算はしない）
  amountJPY?: number; // 課金金額（円）
};

type PlanDetail = {
  id: number;
  name: string;
  publishAt?: string;
  publishTime?: string; // ★ 念のため
  picks?: string[];
  notes?: string;
  detailsText?: string;
  recommendBet?: string;
  recommendBetAmount?: string;
  recommendAmount?: string;
  recommendedBet?: string;
};

type Review = {
  id: string;
  planId: number;
  text: string;
  createdAt: string;
  updatedAt?: string;
  nickname?: string;
  gender?: string;
  rating?: number;
};

type ReviewForm = {
  nickname: string;
  gender: string; // "male" | "female" 相当
  rating: number; // 1〜5
};

/* =========================
 * フォールバック対応 fetch
 * ========================= */
async function fetchJsonWithFallback(
  base: string,
  candidates: string[],
  opts?: RequestInit
) {
  for (const path of candidates) {
    const res = await fetch(base.replace(/\/$/, "") + path, {
      credentials: "include",
      cache: "no-store",
      ...(opts || {}),
    });
    if (res.ok) return res.json();
    if (res.status !== 404) {
      const txt = await res.text().catch(() => "");
      throw new Error(`${path} ${res.status} ${txt}`);
    }
  }
  throw new Error("全候補404");
}

/* =========================
 * 共通ユーティリティ
 * ========================= */
const fmtYmdHms = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
};

// 文字列 publishAt を Date 正規化（"YYYY-MM-DD HH:mm" にも軽く対応）
const toPublishDate = (s?: string) => {
  if (!s) return null;
  const raw = String(s).trim();
  if (!raw) return null;

  let normalized = raw;

  // "YYYY-MM-DD HH:mm" → "YYYY-MM-DDTHH:mm:00"
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(raw)) {
    normalized = raw.replace(" ", "T") + ":00";
  }

  const d = new Date(normalized);
  if (isNaN(d.getTime())) return null;
  return d;
};

// ★ publishAt が無いときは「公開済み扱い」にする
const isPublished = (nowISO: string, publishAt?: string) => {
  if (!publishAt) return true; // 公開日時未設定 = いつでも公開
  const now = new Date(nowISO);
  const target = toPublishDate(publishAt);
  if (!target) return true; // 変なフォーマットの場合もとりあえず公開扱い
  return now.getTime() >= target.getTime();
};

const makeStars = (rating?: number) => {
  const r = Math.max(0, Math.min(5, rating || 0));
  return "★★★★★".slice(0, r) + "☆☆☆☆☆".slice(0, 5 - r);
};

/* =========================
 * 公開までのカウントダウン
 * ========================= */
function Countdown({ targetISO }: { targetISO?: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetISO) return null;
  const target = toPublishDate(targetISO);
  if (!target) return <span>公開済み</span>;

  const diff = target.getTime() - now;
  if (diff <= 0) return <span>公開済み</span>;

  const sec = Math.floor(diff / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;

  return (
    <span>
      {d > 0 ? `${d}日 ` : ""}
      {String(h).padStart(2, "0")}:
      {String(m).padStart(2, "0")}:
      {String(s).padStart(2, "0")}
    </span>
  );
}

/* =========================
 * 画面本体
 * ========================= */
export default function MyPage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [user, setUser] = useState<UserInfo | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);

  // プラン詳細（planId キー）
  const [details, setDetails] = useState<
    Record<number, PlanDetail | "loading" | "error">
  >({});

  // プランマスタ情報（公開時刻など）
  const [plansById, setPlansById] = useState<
    Record<number, { publishAt?: string; publishTime?: string }>
  >({});

  // レビュー情報（planId ごと）
  const [reviews, setReviews] = useState<Record<number, Review | null>>({});

  // レビュー入力値・送信状態（planId ごと） ※コメント本文
  const [reviewInputs, setReviewInputs] = useState<Record<number, string>>({});
  const [reviewSaving, setReviewSaving] = useState<Record<number, boolean>>({});

  // レビュー編集欄の開閉（planId ごと）
  const [reviewEditorOpen, setReviewEditorOpen] = useState<
    Record<number, boolean>
  >({});

  // スマホ用レビュー入力フォーム値（planId ごと）
  const [reviewForms, setReviewForms] = useState<Record<number, ReviewForm>>(
    {}
  );

  // 開いている行は「購入ID」で管理（★個別に開く）
  const [openRow, setOpenRow] = useState<string | number | null>(null);

  // プランごとのタブ状態（0: 公開状況, 1: 買い目）
  const [tabByPlan, setTabByPlan] = useState<Record<number, number>>({});
  const getTab = (planId: number) => tabByPlan[planId] ?? 0;
  const setTabFor = (planId: number, v: number) =>
    setTabByPlan((m) => ({ ...m, [planId]: v }));

  // セクション開閉
  const [openPlanSection, setOpenPlanSection] = useState(true);
  const [openPointSection, setOpenPointSection] = useState(false);

  /* =========================
   * 初回ロード
   * ========================= */
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        // ① ログインユーザー情報
        const uJson = await fetchJsonWithFallback(API_BASE, [
          "/api/auth/me",
          "/api/user/info",
          "/api/users/me",
          "/api/auth/session",
        ]);
        const currentUser: UserInfo = uJson?.user || uJson || {};
        const uid =
          (currentUser.userId as any) ?? (currentUser.id as any) ?? null;

        // ② 購入履歴（admin history も候補に含める）
        let pJson: any = null;
        try {
          pJson = await fetchJsonWithFallback(API_BASE, [
            "/api/orders/mine", // 想定1
            "/api/user/purchases",
            "/api/user/orders",
            "/api/orders",
            "/api/admin/orders/history", // 管理画面用をフォールバック利用
          ]);
        } catch (e) {
          console.warn("purchases fetch failed, treat as empty", e);
          pJson = [];
        }

        if (!aborted) {
          setUser(currentUser);

          // ③ 形を統一してフィルタ（購入履歴）
          let rows: any[] = [];
          if (Array.isArray(pJson)) rows = pJson;
          else if (Array.isArray(pJson.items)) rows = pJson.items;
          else if (Array.isArray(pJson.orders)) rows = pJson.orders;
          else if (Array.isArray(pJson.rows)) rows = pJson.rows;

          if (uid != null) {
            rows = rows.filter(
              (r) =>
                String((r as any).userId ?? (r as any).uid ?? "") ===
                String(uid)
            );
          }

          const mapped: Purchase[] = rows.map((r: any) => {
            const rawName = String(
              r.planName ?? r.plan_name ?? r.plan?.name ?? "プラン名不明"
            );
            const rawPlanId = Number(r.planId ?? r.plan_id ?? 0);

            // 課金金額（円）だけそのまま拾う
            const amountJPY =
              Number(
                r.priceJPY ?? r.amountJPY ?? r.money ?? r.amount ?? 0
              ) || undefined;

            // ★ ポイント購入かどうか判定（レコードの種別だけ）
            const isPointPurchase =
              !rawPlanId ||
              /ポイント/.test(rawName) ||
              r.type === "points" ||
              r.type === "point_purchase";

            // ★ pt は「サーバーが明示している場合のみ」拾う。金額→pt の計算は絶対にしない
            const explicitPoint =
              r.pointAmount ??
              r.points ??
              r.point ??
              r.amountPoint ??
              r.amount_points ??
              undefined;

            const pointAmount =
              explicitPoint != null && explicitPoint !== ""
                ? Number(explicitPoint) || undefined
                : undefined;

            return {
              id: r.id ?? r.orderId ?? "",
              planId: rawPlanId,
              planName: rawName,
              purchasedAt:
                r.purchasedAt ||
                r.createdAt ||
                r.date ||
                new Date().toISOString(),
              publishAt:
                r.publishAt || r.openAt || r.plan?.publishAt || undefined,
              publishTime: r.publishTime || r.plan?.publishTime || undefined,
              isPointPurchase,
              pointAmount,
              amountJPY,
            };
          });

          setPurchases(mapped);
        }

        // ④ プランマスタ（公開時刻など）も取得
        let plansJson: any = null;
        try {
          plansJson = await fetchJsonWithFallback(API_BASE, [
            "/api/public/plans",
            "/api/admin/plans",
          ]);
        } catch (e) {
          console.warn("plans fetch failed, ignore", e);
          plansJson = null;
        }

        if (!aborted && plansJson) {
          let planRows: any[] = [];
          if (Array.isArray(plansJson)) planRows = plansJson;
          else if (Array.isArray(plansJson.plans)) planRows = plansJson.plans;
          else if (Array.isArray(plansJson.items)) planRows = plansJson.items;

          // ★ publishAt / publishTime 両方を埋める
          const map: Record<
            number,
            { publishAt?: string; publishTime?: string }
          > = {};
          for (const r of planRows) {
            const pid = Number(r.id ?? r.planId ?? r.plan_id);
            if (!pid) continue;

            const baseTime =
              r.publishAt ||
              r.publishTime ||
              r.openAt ||
              r.open_time ||
              (r as any).publish_time ||
              undefined;

            map[pid] = {
              publishAt: baseTime,
              publishTime: r.publishTime || r.publishAt || baseTime,
            };
          }

          setPlansById(map);
        }

        // ⑤ レビュー一覧（自分の分）を取得
        let revJson: any = null;
        try {
          revJson = await fetchJsonWithFallback(API_BASE, [
            "/api/reviews/mine",
          ]);
        } catch (e) {
          console.warn("reviews fetch failed, ignore", e);
          revJson = null;
        }

        if (!aborted && revJson) {
          let revRows: any[] = [];
          if (Array.isArray(revJson)) revRows = revJson;
          else if (Array.isArray(revJson.items)) revRows = revJson.items;
          else if (Array.isArray(revJson.reviews)) revRows = revJson.reviews;

          const revMap: Record<number, Review | null> = {};
          for (const r of revRows) {
            const pid = Number(r.planId ?? r.plan_id ?? r.plan?.id);
            if (!pid) continue;
            revMap[pid] = {
              id: String(r.id ?? r.reviewId ?? ""),
              planId: pid,
              text: String(r.text ?? r.comment ?? ""),
              createdAt: r.createdAt || r.date || new Date().toISOString(),
              updatedAt: r.updatedAt,
              nickname:
                r.nickname ??
                r.author ??
                r.name ??
                (r.userName as string) ??
                "",
              gender: r.gender ?? r.sex ?? "",
              rating:
                Number(
                  r.rating ??
                    r.score ??
                    r.level ??
                    r.recommendLevel ??
                    r.stars ??
                    0
                ) || undefined,
            };
          }
          setReviews(revMap);
        }
      } catch (e: any) {
        if (!aborted) setErr(e?.message || "読み込みに失敗しました");
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  /* =========================
   * 買い目詳細取得（/detail → /本体 フォールバック）
   * ========================= */
  const openDetail = async (planId: number) => {
    // 既に取得済みなら再取得しない（エラー時のみ再トライ可）
    if (details[planId] && details[planId] !== "error") return;

    setDetails((m) => ({ ...m, [planId]: "loading" }));
    try {
      const json = await fetchJsonWithFallback(API_BASE, [
        `/api/public/plans/${planId}/detail`,
        `/api/public/plans/${planId}`,
        `/api/admin/plans/${planId}`, // ★ 最終フォールバック
      ]);
      setDetails((m) => ({ ...m, [planId]: json }));
    } catch (e) {
      console.warn("detail fetch failed", e);
      setDetails((m) => ({ ...m, [planId]: "error" }));
    }
  };

  const handleToggleRow = (rowId: string | number, planId: number) => {
    const isOpen = openRow === rowId;
    setOpenRow(isOpen ? null : rowId);

    // 開くときだけ詳細取得（planId単位で1回）
    if (!isOpen) {
      void openDetail(planId);
    }
  };

  // プラン購入 / ポイント購入 に分割
  const planPurchases = useMemo(
    () => purchases.filter((p) => !p.isPointPurchase),
    [purchases]
  );
  const pointPurchases = useMemo(
    () => purchases.filter((p) => p.isPointPurchase),
    [purchases]
  );

  const totalPurchased = useMemo(
    () => planPurchases.length,
    [planPurchases]
  );

  /* =========================
   * レビュー入力ハンドラ
   * ========================= */
  // コメント本文
  const handleReviewInputChange = (planId: number, text: string) => {
    setReviewInputs((prev) => ({ ...prev, [planId]: text }));
  };

  // スマホフォーム用（ニックネーム・性別・評価）
  const handleReviewFormChange = (
    planId: number,
    field: keyof ReviewForm,
    value: string | number
  ) => {
    setReviewForms((prev) => {
      const base: ReviewForm =
        prev[planId] || {
          nickname: (user?.name as string) || "",
          gender: "",
          rating: 5,
        };
      return {
        ...prev,
        [planId]: {
          ...base,
          [field]: value,
        },
      };
    });
  };

  // ▼ レビュー編集欄の開閉：開くときにフォーム初期値を注入
  const toggleReviewEditor = (planId: number) => {
    setReviewEditorOpen((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));

    setReviewForms((prev) => {
      if (prev[planId]) return prev;
      const base = reviews[planId];
      return {
        ...prev,
        [planId]: {
          nickname:
            base?.nickname || (user?.name as string) || "",
          gender: base?.gender || "",
          rating: base?.rating || 5,
        },
      };
    });

    // コメント本文も、既存レビューがあれば初期値にしておく
    setReviewInputs((prev) => {
      if (prev[planId] != null) return prev;
      const base = reviews[planId];
      if (!base?.text) return prev;
      return {
        ...prev,
        [planId]: base.text,
      };
    });
  };

  const saveReview = async (planId: number) => {
    const form = reviewForms[planId];
    const currentText =
      reviewInputs[planId] ?? reviews[planId]?.text ?? "";
    const text = currentText.trim();
    if (!text) return;

    const nickname = form?.nickname || "";
    const gender = form?.gender || "";
    const rating = form?.rating ?? 0;

    setReviewSaving((prev) => ({ ...prev, [planId]: true }));
    try {
      const res = await fetch(API_BASE.replace(/\/$/, "") + "/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          planId,
          text,
          nickname,
          gender,
          rating,
          // バックエンドのどのキーを見ていても拾えるように多めに送る
          score: rating,
          level: rating,
          recommendLevel: rating,
        }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`/api/reviews ${res.status} ${txt}`);
      }
      const body = await res.json().catch(() => ({} as any));
      const nowIso = new Date().toISOString();

      setReviews((prev) => ({
        ...prev,
        [planId]: {
          id: String(
            (body as any).id ??
              (body as any).reviewId ??
              prev[planId]?.id ??
              ""
          ),
          planId,
          text,
          createdAt: prev[planId]?.createdAt || nowIso,
          updatedAt: nowIso,
          nickname,
          gender,
          rating,
        },
      }));
      setReviewInputs((prev) => ({ ...prev, [planId]: "" }));
      setReviewEditorOpen((prev) => ({ ...prev, [planId]: false }));
    } catch (e: any) {
      setErr(e?.message || "レビューの送信に失敗しました");
    } finally {
      setReviewSaving((prev) => ({ ...prev, [planId]: false }));
    }
  };

  /* =========================
   * 表示
   * ========================= */
  return (
    <main className="noGlobalBg">
      <Header />

      <Box sx={{ p: 3, pb: 6 }}>
        {/* マイページ用ヘッダー画像（new-mypage.webp）＋タイトル重ね */}
        <Box sx={{ mb: 2, position: "relative" }}>
          <Box
            component="img"
            src="/assets/member/new-mypage.webp"
            alt="My page"
            sx={{ width: "100%", display: "block", borderRadius: 1 }}
          />

          {/* タイトル（大きく中央／ゴールド点滅） */}
<Typography
  sx={{
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textAlign: "center",
    whiteSpace: "nowrap",
    fontSize: { xs: 34, sm: 44, md: 52 },
    color: "#ffffff",
    textShadow: "0 2px 10px rgba(0,0,0,.65)",
    pointerEvents: "none",
  }}
>
  マイページ
</Typography>

          {/* サブコピー：常時金＋左→右に光が流れる → 全点灯で3秒停止 → ループ */}
          <Typography
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, calc(-50% + 40px))",
              fontWeight: 800,
              letterSpacing: "0.06em",
              textAlign: "center",
              whiteSpace: "nowrap",
              fontSize: { xs: 22, sm: 30, md: 36 },
              color: "#d7b56d", // ← ベースは常時表示
              textShadow:
                "0 2px 10px rgba(0,0,0,.55), 0 0 18px rgba(215,181,109,.35)",
              pointerEvents: "none",

              // 光の流れ（疑似要素で上乗せ）
              "&::after": {
                content: '"BOATOKUMANの戦略型予想"',
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: "100%",
                color: "transparent",
                backgroundImage:
                  "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.95) 45%, rgba(255,255,255,0) 90%)",
                backgroundSize: "220% 100%",
                backgroundPosition: "-120% 50%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 10px rgba(255,255,255,.35))",
                animation: "mypageSweepHold 5.6s linear infinite",
              },
            }}
          >
            BOATOKUMANの戦略型予想
          </Typography>

          {/* アニメ定義（MyPage内に局所定義） */}
          <Box
            sx={{
              "@keyframes mypageGoldBlink": {
                "0%, 100%": { opacity: 1, filter: "brightness(1)" },
                "50%": { opacity: 0.55, filter: "brightness(1.2)" },
              },
              // 左→右に光が走る（約2.6秒）→ そのまま3秒停止 → ループ（合計5.6秒）
              "@keyframes mypageSweepHold": {
                "0%": { backgroundPosition: "-120% 50%", opacity: 0 },
                "10%": { opacity: 1 },
                "46%": { backgroundPosition: "120% 50%", opacity: 1 }, // ← ここまでが「流れる」
                "46.01%": { backgroundPosition: "120% 50%", opacity: 0 }, // ← 走り終わったら光だけ消す
                "100%": { backgroundPosition: "120% 50%", opacity: 0 }, // ← 3秒停止
              },
            }}
          />
        </Box>

        <Typography sx={{ mb: 2, fontSize: 14, color: "#555" }}>
          購入プラン数：{totalPurchased} 件
        </Typography>

        {/* 購入プランセクション */}
        <Paper sx={{ p: 2, mb: 3 }}>
          {/* ヘッダー帯 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
background: "linear-gradient(135deg, #caa84f 0%, #f5e6b3 40%, #b8902f 100%)",
color: "#1a1a1a",
boxShadow: "0 2px 8px rgba(0,0,0,.25)",
border: "1px solid rgba(255,255,255,.25)",
              borderRadius: "8px",
              px: 2,
              py: 1,
              mb: openPlanSection ? 1 : 0,
            }}
          >
            <Typography sx={{ fontWeight: "bold", fontSize: 16 }}>
              購入プラン
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => setOpenPlanSection((v) => !v)}
              sx={{
                minWidth: 0,
                color: "#fff",
                fontSize: 20,
                fontWeight: "bold",
                lineHeight: 1,
              }}
            >
              {openPlanSection ? "－" : "＋"}
            </Button>
          </Box>

          {openPlanSection && (
            <>
              {loading && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <CircularProgress size={18} /> 読み込み中…
                </Stack>
              )}

              {err && !loading && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {err}
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>プラン名</TableCell>
                      <TableCell>購入日</TableCell>
                      <TableCell align="right">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {planPurchases.length === 0 && !loading && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ color: "#777" }}
                        >
                          購入データがありません
                        </TableCell>
                      </TableRow>
                    )}

                    {planPurchases.map((p) => {
                      const detail = details[p.planId];
                      const master = plansById[p.planId];

                      // 購入レコード → プランマスタ → 詳細API の順で publishAt/publishTime を参照
                      const publishISO =
                        p.publishAt ||
                        p.publishTime ||
                        master?.publishAt ||
                        master?.publishTime ||
                        (typeof detail === "object"
                          ? detail?.publishAt || detail?.publishTime
                          : undefined);

                      const published = isPublished(
                        new Date().toISOString(),
                        publishISO
                      );

                      // ★ 購入日時から3日（72時間）の閲覧期限
                      let expired = false;
                      if (p.purchasedAt) {
                        const purchasedMs = new Date(
                          p.purchasedAt
                        ).getTime();
                        if (!isNaN(purchasedMs)) {
                          const nowMs = Date.now();
                          const limitMs =
                            purchasedMs + 3 * 24 * 60 * 60 * 1000; // 3日
                          expired = nowMs > limitMs;
                        }
                      }

                      const review = reviews[p.planId] || null;
                      const reviewInputValue =
                        reviewInputs[p.planId] ?? review?.text ?? "";
                      const reviewForm: ReviewForm =
                        reviewForms[p.planId] || {
                          nickname: (user?.name as string) || "",
                          gender: "",
                          rating: 5,
                        };

                      return (
                        <React.Fragment key={`${p.id}-${p.planId}`}>
                          {/* 1行目：プラン名＋購入日＋ボタン */}
                          <TableRow>
                            <TableCell>{p.planName}</TableCell>
                            <TableCell>{fmtYmdHms(p.purchasedAt)}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="contained"
                                disabled={expired}
                                onClick={() => {
                                  if (!expired) {
                                    handleToggleRow(p.id, p.planId);
                                  }
                                }}
                                sx={{
                                  backgroundColor: expired
                                    ? "#9e9e9e"
                                    : undefined,
                                }}
                              >
                                {expired
                                  ? "閲覧期限切れ"
                                  : openRow === p.id
                                  ? "閉じる"
                                  : "買い目を見る"}
                              </Button>
                            </TableCell>
                          </TableRow>

                          {/* 2行目：詳細エリア（開いているときだけ表示） */}
                          {openRow === p.id && (
                            <TableRow>
                              <TableCell colSpan={3}>
                                {/* 読み込み・エラー・成功で分岐 */}
                                {!detail || detail === "loading" ? (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <CircularProgress size={16} /> 読み込み中…
                                  </Stack>
                                ) : detail === "error" ? (
                                  <Alert severity="info">
                                    {publishISO ? (
                                      published ? (
                                        <>
                                          公開済みですが、買い目情報はまだ登録されていません。
                                          <br />
                                          公開時刻：
                                          {fmtYmdHms(publishISO)}
                                        </>
                                      ) : (
                                        <>
                                          公開前です（残り{" "}
                                          <Countdown targetISO={publishISO} />
                                          ）<br />
                                          公開時刻：
                                          {fmtYmdHms(publishISO)}
                                        </>
                                      )
                                    ) : (
                                      <>
                                        現在、このプランの買い目情報はまだ登録されていません。
                                        公開時間以降に順次表示されます。
                                      </>
                                    )}
                                  </Alert>
                                ) : (
                                  <Box sx={{ p: 1 }}>
                                    <Tabs
                                      value={getTab(p.planId)}
                                      onChange={(_, v) =>
                                        setTabFor(p.planId, v)
                                      }
                                      sx={{ mb: 2 }}
                                      variant="fullWidth"
                                    >
                                      <Tab label="公開状況" />
                                      <Tab label="買い目" />
                                    </Tabs>

                                    {/* 公開状況タブ */}
                                    {getTab(p.planId) === 0 && (
                                      <>
                                        {published ? (
                                          <Typography>
                                            公開済みです。
                                            <br />
                                            公開時刻：
                                            {publishISO
                                              ? fmtYmdHms(publishISO)
                                              : "-"}
                                          </Typography>
                                        ) : (
                                          <Typography sx={{ color: "#666" }}>
                                            公開前です（残り{" "}
                                            <Countdown
                                              targetISO={publishISO}
                                            />
                                            ）<br />
                                            公開時刻：
                                            {publishISO
                                              ? fmtYmdHms(publishISO)
                                              : "-"}
                                          </Typography>
                                        )}
                                      </>
                                    )}

                                    {/* 買い目タブ ＋ レビュー */}
                                    {getTab(p.planId) === 1 &&
                                      (() => {
                                        const d: any = detail || {};
                                        const picks: string[] = Array.isArray(
                                          d.picks
                                        )
                                          ? d.picks
                                          : [];
                                        const extraText: string =
                                          d.detailsText || d.notes || ""; // ★ detailsText 優先

                                        const recommendBet: string | undefined =
                                          d.recommendBet ||
                                          d.recommendBetAmount ||
                                          d.recommendAmount ||
                                          d.recommendedBet ||
                                          undefined;

                                        const hasPicks = picks.length > 0;
                                        const hasText = !!extraText;
                                        const hasAny =
                                          hasPicks || hasText || !!recommendBet;

                                        // ★ 公開前は、中身があっても絶対に表示しない
                                        if (!published) {
                                          return (
                                            <Typography
                                              sx={{ color: "#666" }}
                                            >
                                              公開前のため、買い目はまだ表示されません。
                                            </Typography>
                                          );
                                        }

                                        // 公開済みだが中身が無い
                                        if (!hasAny) {
                                          return (
                                            <Typography
                                              sx={{ color: "#666" }}
                                            >
                                              買い目データがありません。
                                            </Typography>
                                          );
                                        }

                                        // 中身あり → 表示
                                        return (
                                          <Box>
                                            <Typography
                                              variant="subtitle1"
                                              gutterBottom
                                            >
                                              買い目
                                            </Typography>

                                            {hasPicks && (
                                              <ul
                                                style={{
                                                  margin: "8px 0 0 16px",
                                                  padding: 0,
                                                }}
                                              >
                                                {picks.map((s, i) => (
                                                  <li key={i}>
                                                    <Typography>
                                                      {s}
                                                    </Typography>
                                                  </li>
                                                ))}
                                              </ul>
                                            )}

                                            {hasText && (
                                              <Typography
                                                sx={{
                                                  mt: hasPicks ? 1 : 0,
                                                  whiteSpace: "pre-wrap",
                                                  fontSize: 13,
                                                }}
                                              >
                                                {extraText}
                                              </Typography>
                                            )}

                                            {recommendBet && (
                                              <Typography
                                                sx={{
                                                  mt: 1,
                                                  fontSize: 13,
                                                  fontWeight: 600,
                                                  color: "#1a202c",
                                                }}
                                              >
                                                推奨賭け金：{recommendBet}
                                              </Typography>
                                            )}

                                            {/* ▼ レビュー：買い目の一番下にボタン＋入力欄 */}
                                            <Box
                                              sx={{
                                                mt: 3,
                                                p: 1.5,
                                                borderRadius: 1,
                                                border: "1px solid #e2e8f0",
                                                backgroundColor: "#f9fafb",
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ fontSize: 13, mb: 1 }}
                                              >
                                                このプランのレビュー
                                              </Typography>

                                              {!published ? (
                                                <Typography
                                                  sx={{
                                                    fontSize: 12,
                                                    color: "#666",
                                                  }}
                                                >
                                                  公開後にレビューを投稿できます。
                                                </Typography>
                                              ) : (
                                                <>
                                                  {review ? (
                                                    <>
                                                      {review.rating ? (
                                                        <Typography
                                                          sx={{
                                                            fontSize: 12,
                                                            mb: 0.5,
                                                          }}
                                                        >
                                                          {makeStars(
                                                            review.rating
                                                          )}
                                                        </Typography>
                                                      ) : null}
                                                      {review.nickname ? (
                                                        <Typography
                                                          sx={{
                                                            fontSize: 11,
                                                            color: "#666",
                                                            mb: 0.5,
                                                          }}
                                                        >
                                                          投稿者：
                                                          {review.nickname}
                                                        </Typography>
                                                      ) : null}
                                                      <Typography
                                                        sx={{
                                                          fontSize: 12,
                                                          whiteSpace:
                                                            "pre-wrap",
                                                          mb: 0.5,
                                                        }}
                                                      >
                                                        {review.text ||
                                                          "レビュー本文は登録されていません。"}
                                                      </Typography>
                                                      <Typography
                                                        sx={{
                                                          fontSize: 11,
                                                          color: "#666",
                                                        }}
                                                      >
                                                        投稿日時：
                                                        {fmtYmdHms(
                                                          review.updatedAt ||
                                                            review.createdAt
                                                        )}
                                                      </Typography>
                                                    </>
                                                  ) : (
                                                    <Typography
                                                      sx={{
                                                        fontSize: 12,
                                                        color: "#666",
                                                      }}
                                                    >
                                                      まだレビューは登録されていません。
                                                    </Typography>
                                                  )}

                                                  {/* レビューを書くボタン */}
                                                  <Box
                                                    sx={{
                                                      mt: 1,
                                                      textAlign: "right",
                                                    }}
                                                  >
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={() =>
                                                        toggleReviewEditor(
                                                          p.planId
                                                        )
                                                      }
                                                    >
                                                      {reviewEditorOpen[
                                                        p.planId
                                                      ]
                                                        ? "入力欄を閉じる"
                                                        : "レビューを書く"}
                                                    </Button>
                                                  </Box>

                                                  {/* 入力欄（スマホ用フォーム：トグル） */}
                                                  {reviewEditorOpen[
                                                    p.planId
                                                  ] && (
                                                    <Box
                                                      sx={{
                                                        mt: 2,
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        backgroundColor:
                                                          "#ffffff",
                                                        border:
                                                          "1px solid #e2e8f0",
                                                      }}
                                                    >
                                                      {/* プラン名は固定表示 */}
                                                      <Typography
                                                        sx={{
                                                          fontSize: 12,
                                                          mb: 0.5,
                                                        }}
                                                      >
                                                        購入プラン（商品名）：
                                                      </Typography>
                                                      <Typography
                                                        sx={{
                                                          fontSize: 13,
                                                          fontWeight: 600,
                                                          mb: 1.5,
                                                        }}
                                                      >
                                                        {p.planName}
                                                      </Typography>

                                                      {/* おすすめレベル（★） */}
                                                      <Typography
                                                        sx={{
                                                          fontSize: 12,
                                                          mb: 0.5,
                                                        }}
                                                      >
                                                        おすすめレベル：
                                                      </Typography>
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          gap: 0.5,
                                                          mb: 1.5,
                                                        }}
                                                      >
                                                        {[1, 2, 3, 4, 5].map(
                                                          (star) => (
                                                            <Button
                                                              key={star}
                                                              size="small"
                                                              variant={
                                                                reviewForm.rating >=
                                                                star
                                                                  ? "contained"
                                                                  : "outlined"
                                                              }
                                                              onClick={() =>
                                                                handleReviewFormChange(
                                                                  p.planId,
                                                                  "rating",
                                                                  star
                                                                )
                                                              }
                                                              sx={{
                                                                minWidth: 32,
                                                                px: 0,
                                                                fontSize: 16,
                                                                lineHeight: 1,
                                                              }}
                                                            >
                                                              {reviewForm.rating >=
                                                              star
                                                                ? "★"
                                                                : "☆"}
                                                            </Button>
                                                          )
                                                        )}
                                                      </Box>

                                                      {/* 投稿者（ニックネーム） */}
                                                      <TextField
                                                        label="投稿者"
                                                        placeholder="例：レビュー太郎"
                                                        fullWidth
                                                        size="small"
                                                        value={
                                                          reviewForm.nickname
                                                        }
                                                        onChange={(e) =>
                                                          handleReviewFormChange(
                                                            p.planId,
                                                            "nickname",
                                                            e.target.value
                                                          )
                                                        }
                                                        sx={{ mb: 1.5 }}
                                                      />

                                                      {/* 性別 */}
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 1,
                                                          mb: 1.5,
                                                        }}
                                                      >
                                                        <Typography
                                                          sx={{
                                                            fontSize: 12,
                                                          }}
                                                        >
                                                          性別：
                                                        </Typography>
                                                        <Button
                                                          size="small"
                                                          variant={
                                                            reviewForm.gender ===
                                                            "male"
                                                              ? "contained"
                                                              : "outlined"
                                                          }
                                                          onClick={() =>
                                                            handleReviewFormChange(
                                                              p.planId,
                                                              "gender",
                                                              "male"
                                                            )
                                                          }
                                                        >
                                                          男性
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant={
                                                            reviewForm.gender ===
                                                            "female"
                                                              ? "contained"
                                                              : "outlined"
                                                          }
                                                          onClick={() =>
                                                            handleReviewFormChange(
                                                              p.planId,
                                                              "gender",
                                                              "female"
                                                            )
                                                          }
                                                        >
                                                          女性
                                                        </Button>
                                                      </Box>

                                                      {/* コメント */}
                                                      <TextField
                                                        label="コメント"
                                                        placeholder="プランの感想や評価を記入"
                                                        multiline
                                                        minRows={3}
                                                        fullWidth
                                                        size="small"
                                                        value={reviewInputValue}
                                                        onChange={(e) =>
                                                          handleReviewInputChange(
                                                            p.planId,
                                                            e.target.value
                                                          )
                                                        }
                                                      />

                                                      <Box
                                                        sx={{
                                                          textAlign: "right",
                                                          mt: 1.5,
                                                        }}
                                                      >
                                                        <Button
                                                          size="small"
                                                          variant="contained"
                                                          disabled={
                                                            reviewSaving[
                                                              p.planId
                                                            ] ||
                                                            !reviewInputValue.trim()
                                                          }
                                                          onClick={() =>
                                                            saveReview(
                                                              p.planId
                                                            )
                                                          }
                                                        >
                                                          {reviewSaving[
                                                            p.planId
                                                          ]
                                                            ? "送信中…"
                                                            : review
                                                            ? "レビューを更新"
                                                            : "レビューを投稿"}
                                                        </Button>
                                                      </Box>
                                                    </Box>
                                                  )}
                                                </>
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      })()}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>

        {/* ポイント購入履歴セクション */}
        {pointPurchases.length > 0 && (
          <Paper sx={{ p: 2 }}>
            {/* ヘッダー帯 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
background: "linear-gradient(135deg, #b8902f 0%, #f5e6b3 45%, #caa84f 100%)",
color: "#1a1a1a",
boxShadow: "0 2px 8px rgba(0,0,0,.25)",
border: "1px solid rgba(255,255,255,.25)",
                borderRadius: "8px",
                px: 2,
                py: 1,
                mb: openPointSection ? 1 : 0,
              }}
            >
              <Typography sx={{ fontWeight: "bold", fontSize: 16 }}>
                ポイント購入履歴
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => setOpenPointSection((v) => !v)}
                sx={{
                  minWidth: 0,
                  color: "#fff",
                  fontSize: 20,
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                {openPointSection ? "－" : "＋"}
              </Button>
            </Box>

            {openPointSection && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>内容</TableCell>
                      <TableCell>購入日</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pointPurchases.map((p) => (
                      <TableRow key={`${p.id}-${p.planId}-point`}>
                        <TableCell>
                          {p.planName || "ポイント購入"}
                          {p.amountJPY != null &&
                            `（${p.amountJPY.toLocaleString()} 円）`}
                        </TableCell>
                        <TableCell>{fmtYmdHms(p.purchasedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>

      <Footer />
    </main>
  );
}
